import { useEffect } from 'react';
import { Dialog } from '@mui/material';
import styled from 'styled-components';
import { Alert, Button, Typography, colors } from '@leapeasy/ui-kit';
import { useDispatch, useSelector } from 'react-redux';
import { sendResetEmailAction, setError, setResetResult } from 'store/actions/authActions';
import resetPasswordMailSvg from 'assets/reset-password-mail.svg';

export const ResetPasswordModal = ({ open, onClose, defaultEmail = '' }) => {
  const dispatch = useDispatch();
  const loading = useSelector((state) => state.getIn(['auth', 'loading']));
  const error = useSelector((state) => state.getIn(['auth', 'error']));
  const resetResult = useSelector((state) => state.getIn(['auth', 'resetResult']));

  useEffect(() => {
    if (!open) return;
    dispatch(setError(''));
    dispatch(setResetResult(''));
    if (defaultEmail) {
      dispatch(sendResetEmailAction({ email: defaultEmail }));
    }
  }, [open, defaultEmail, dispatch]);

  const handleClose = () => {
    dispatch(setError(''));
    dispatch(setResetResult(''));
    onClose();
  };

  return (
    <StyledDialog open={open} onClose={handleClose} maxWidth={false}>
      <Container>
        <Illustration
          src={resetPasswordMailSvg}
          alt="Reset password email sent"
          width="113"
          height="111"
        />

        <Title variant="body1">Password reset email sent</Title>

        <Description>
          <p>A reset link has been sent to your email address.</p>
          <p>
            If you don&apos;t see it within a few minutes, check your spam folder or resend the
            email.
          </p>
        </Description>

        <ResendLink
          type="button"
          onClick={() => {
            if (defaultEmail) {
              dispatch(sendResetEmailAction({ email: defaultEmail }));
            }
          }}
          disabled={!defaultEmail || loading}
        >
          Resend email
        </ResendLink>

        {!!error && (
          <Alert
            type="error"
            variant="contained"
            showBorder
            description={error}
            showClose={false}
          />
        )}

        {loading && resetResult !== 'success' && (
          <LoadingText variant="body3">Sending reset email...</LoadingText>
        )}

        <PrimaryActionButton variant="primary" size="medium" onClick={handleClose}>
          Continue
        </PrimaryActionButton>
      </Container>
    </StyledDialog>
  );
};

const StyledDialog = styled(Dialog)`
  .MuiDialog-paper {
    margin: 0;
    border-radius: 12px;
    overflow: hidden;
    box-shadow: none;
    background: transparent;
    width: min(351px, calc(100vw - 24px));
    max-width: calc(100vw - 24px);
  }
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 40px 20px 20px;
  background: #fff;
  border: 1px solid #f3f3f3;
  box-shadow:
    0px 16px 32px 0px rgba(90, 94, 107, 0.04),
    0px 2px 4px 0px rgba(90, 94, 107, 0.08);
  border-radius: 12px;
  box-sizing: border-box;
  width: 100%;
  min-height: 307px;
`;

const Illustration = styled.img`
  width: 113px;
  height: 111px;
`;

const Title = styled(Typography)`
  color: #2e0f40;
  font-weight: 600 !important;
  text-align: center;
  font-size: 14px !important;
  line-height: 165% !important;
  letter-spacing: -0.14px;
`;

const LoadingText = styled(Typography)`
  color: ${colors.neutral[900]};
`;

const Description = styled.div`
  margin: 0;
  color: #6a5e71;
  font-family: Inter;
  font-size: 12px;
  line-height: 1.65;
  max-width: 311px;
  opacity: 0.9;
  display: flex;
  flex-direction: column;
  gap: 8px;

  p {
    margin: 0;
  }
`;

const ResendLink = styled.button`
  margin: 0;
  padding: 0;
  border: none;
  background: transparent;
  color: ${colors.purple[900]};
  font-family: Inter, sans-serif;
  font-size: 14px;
  font-weight: 600;
  line-height: 1.65;
  text-decoration: underline;
  cursor: pointer;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

const PrimaryActionButton = styled(Button)`
  width: 100%;
  min-height: 40px;
`;
